/**
 * promise-based dialog requiring user to type something to confirm action
 */

import React from "react";
import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  TextField,
  Button,
  Box,
} from "@mui/material";
import Card from 'components/card';
import Icon from 'components/icon';

const useStyles = makeStyles(theme => ({
  root: {
    minWidth: theme.spacing(40),
    backgroundColor: 'transparent',
  },
  titleIcon: {
    marginRight: theme.spacing.dialogTitleIconMargin,
  },
  actionButton: {
    minWidth: theme.spacing(10),
  }
}));

export default function ConfirmByTypingDialog(props) {
  const {
    setConfirm,
    title,
    titleStyle,
    titleIcon,
    requireTypedText,
    content,
  } = props;
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const [promise, setPromise] = React.useState();
  const {register, handleSubmit, formState: { errors } } = useForm();
  const confirmMessage = <>You must type <b>{requireTypedText}</b> to confirm.</>;
  
  const { ref: inputRef, ...inputProps } = register('confirmText', {
    required: true,
    pattern: {
      value: new RegExp(`^${requireTypedText}$`),
      message: confirmMessage
    }
  })

  // the setConfirm prop must be a function that will set this component's confirm method.
  // this allows the parent to open the dialog and wait for a response via a promise.
  React.useEffect(() => {
    if (setConfirm) {
      setConfirm(() => {
        return confirm;
      });
    }
    
  // setConfirm is setter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setConfirm])

  // the confirm method passed to the parent via the setConfirm property.
  // this returns a promise that is resolved when the user chooses a response.
  const confirm = () => {
    return new Promise((resolve, reject) => {
      setPromise({ resolve, reject });
      setOpen(true);
    });
  };

  // ok handler
  const ok = () => {
    promise.resolve(true);
    setOpen(false);
  };

  // cancel handler
  const cancel = () => {
    promise.resolve(false);
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={cancel}
      classes={{paper: classes.root}}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      data-testid={props['data-testid']}
    >
      <form onSubmit={handleSubmit(ok)}>
        <Card
          titleStyle={titleStyle ? titleStyle : "dark"}
          title={<>
            <Icon className={classes.titleIcon} type={titleIcon ? titleIcon : 'info'} />
            {title}
          </>}
        >
          {(content && 
            <Box>
              <DialogContent className={classes.content} id="confirm-dialog-description" >              
                {typeof(content) === 'string' ?
                <DialogContentText style={{marginBottom: 0}}>
                  {content}
                </DialogContentText>
                :
                content}

                {/* required typed text section */}
                {requireTypedText && 
                <Box mt={1}>     
                  <TextField
                    autoFocus
                    variant="outlined"
                    size="small"
                    helperText={confirmMessage}
                    error={errors.confirmText !== undefined}
                    inputRef={inputRef}
                    {...inputProps}
                  />
                </Box>}
                              
              </DialogContent>
            </Box>
          )}
          <DialogActions>
            <Button className={classes.actionButton} onClick={cancel}>
              Cancel
            </Button>
            <Button 
              type="submit"
              className={classes.actionButton} 
              color="primary" 
              autoFocus={!requireTypedText}            
            >
              OK
            </Button>
          </DialogActions>
        </Card>
      </form>  
    </Dialog>
  );
}

ConfirmByTypingDialog.propTypes = {
  setConfirm: PropTypes.func.isRequired,  // function to be assigned by dialog that parent will wait on until user chooses a response
  title: PropTypes.node.isRequired,       // text or JSX to display in dialog title
  titleStyle: PropTypes.string,           // theme to apply to dialog title header (ex: 'error', 'warning')
  titleIcon: PropTypes.string,            // icon to show in dialog header
  content: PropTypes.node,                // any string or JSX to display in the dialog
  requireTypedText: PropTypes.string,     // the case-sensitive string that the user must type to confirm the action
};

ConfirmByTypingDialog.defaultProps = {
  requireTypedText: 'CONFIRM',
}